import { PurchaseRequestAuditStatus } from '../../../interfaces/purchase-request-audit.dto';

export type ActivityFilter = 'all' | 'quote' | 'suppliers' | 'items' | 'comments' | 'system';
export type ActivityGroupKey = Exclude<ActivityFilter, 'all'>;
export type ActivityTone = 'green' | 'blue' | 'amber' | 'red' | 'purple' | 'gray';

export interface ActivityType {
  /** Título curto exibido em destaque; a mensagem do backend segue como corpo. */
  title: string;
  /** Ligadura do Material Symbols. */
  icon: string;
  tone: ActivityTone;
  group: ActivityGroupKey;
}

export const ACTIVITY_FILTERS: ReadonlyArray<{ value: ActivityFilter; label: string }> = [
  { value: 'all', label: 'Tudo' },
  { value: 'quote', label: 'Cotação' },
  { value: 'suppliers', label: 'Fornecedores' },
  { value: 'items', label: 'Itens' },
  { value: 'comments', label: 'Comentários' },
  { value: 'system', label: 'Sistema' }
];

/** Ação de auditoria do backend → apresentação. Chaves são os valores de `PurchaseRequestAuditAction`. */
export const ACTIVITY_TYPES: Readonly<Record<string, ActivityType>> = {
  // Ciclo de vida
  'solicitar-compra': { title: 'Solicitação criada', icon: 'shopping_cart', tone: 'blue', group: 'quote' },
  'iniciar-cotacao': { title: 'Cotação iniciada', icon: 'send', tone: 'blue', group: 'quote' },
  'pedido-de-compra-aceito': { title: 'Pedido aceito', icon: 'check_circle', tone: 'green', group: 'quote' },
  'cancelar-oferta-estruturada': { title: 'Solicitação cancelada', icon: 'cancel', tone: 'red', group: 'quote' },
  'atualizar-status-oferta-estruturada': { title: 'Status alterado', icon: 'swap_horiz', tone: 'amber', group: 'quote' },
  'criar-vinculo-fornecedor-oferta': { title: 'Fornecedor vinculado', icon: 'link', tone: 'green', group: 'suppliers' },

  // Fornecedores
  'adicionar-fornecedor-oferta-estruturada': { title: 'Fornecedor adicionado', icon: 'person_add', tone: 'green', group: 'suppliers' },
  'adicionar-fornecedor-produto': { title: 'Fornecedor adicionado ao produto', icon: 'person_add', tone: 'green', group: 'suppliers' },
  'remover-fornecedor-pedido': { title: 'Fornecedor removido do pedido', icon: 'person_remove', tone: 'red', group: 'suppliers' },
  'remover-fornecedor-produto': { title: 'Fornecedor removido do produto', icon: 'person_remove', tone: 'red', group: 'suppliers' },
  'alterar-comprador-oferta-estruturada': { title: 'Comprador alterado', icon: 'manage_accounts', tone: 'amber', group: 'system' },

  // Cotação
  'criar-trading': { title: 'Cotação respondida', icon: 'sell', tone: 'green', group: 'quote' },
  'processar-planilha-cotacao': { title: 'Planilha de cotação processada', icon: 'table_view', tone: 'blue', group: 'quote' },
  'atualizar-preco-produto': { title: 'Preço atualizado', icon: 'price_change', tone: 'amber', group: 'quote' },
  'atualizar-trading': { title: 'Proposta atualizada', icon: 'edit_note', tone: 'blue', group: 'quote' },

  // Pedidos / itens
  'criar-pedido': { title: 'Item adicionado', icon: 'add_box', tone: 'blue', group: 'items' },
  'remover-pedidos-anteriores': { title: 'Itens anteriores removidos', icon: 'delete_sweep', tone: 'amber', group: 'items' },
  'create-pedidos-anexos': { title: 'Anexo enviado', icon: 'attach_file', tone: 'blue', group: 'items' },
  'alterar-pedidos-atributos': { title: 'Atributos do item alterados', icon: 'tune', tone: 'amber', group: 'items' },

  // Comentários
  'criar-comentario': { title: 'Comentário', icon: 'chat_bubble', tone: 'purple', group: 'comments' },

  // Admin / integração
  'adm-aceita-oferta-estruturada': { title: 'Cotação aprovada', icon: 'verified', tone: 'green', group: 'quote' },
  'formalizar-oferta-estruturada': { title: 'Pedido formalizado', icon: 'assignment_turned_in', tone: 'green', group: 'quote' },
  'upload-planilha-ofertas': { title: 'Planilha de ofertas enviada', icon: 'upload_file', tone: 'blue', group: 'quote' },
  'upload-produtos': { title: 'Produtos importados', icon: 'upload', tone: 'blue', group: 'items' },
  'gerar-foc-comprador': { title: 'FOC do comprador gerado', icon: 'description', tone: 'gray', group: 'system' },
  'gerar-foc-fornecedor': { title: 'FOC do fornecedor gerado', icon: 'description', tone: 'gray', group: 'system' },
  'estornar-protheus': { title: 'Estorno no Protheus', icon: 'undo', tone: 'red', group: 'system' }
};

const DEFAULT_TYPE: ActivityType = { title: 'Atividade', icon: 'info', tone: 'gray', group: 'system' };

/** Resolve a apresentação de um evento; WARN e ERROR do backend sobrepõem o tom e o ícone. */
export function resolveActivityType(action?: string, status?: PurchaseRequestAuditStatus | string): ActivityType {
  const base = (action && ACTIVITY_TYPES[action]) || DEFAULT_TYPE;
  if (status === PurchaseRequestAuditStatus.ERROR) return { ...base, tone: 'red', icon: 'error' };
  if (status === PurchaseRequestAuditStatus.WARN) return { ...base, tone: 'amber', icon: 'warning' };
  return base;
}
